import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { StatusDot } from '../components/StatusDot'
import {
  ApiError, api,
  type Campaign,
  type CampaignEstimate,
  type EmailTemplate,
  type MeResponse,
  type Segment,
  type SegmentValidationReport,
  type TemplateCoverageReport,
} from '../api/client'

const TEST_EMAIL_LS_KEY = 'aamcirc.test_email'
import { fmtInt, fmtRelative } from '../lib/format'

type FormWave = {
  // Form-state representation of a follow-up wave. day_offset and
  // template_id are strings to keep parity with the rest of the form
  // (numeric inputs always start as strings); converted in `body`.
  day_offset: string
  template_id: string  // '' = inherit from prior wave / campaign template
  subject: string      // '' = inherit from prior wave / template subject
}

type FormState = {
  slug: string
  name: string
  template_id: string
  segment_id: string
  // Additional segment IDs as strings to keep parity with segment_id
  // (form-state values are strings; conversion happens in `body`).
  additional_segment_ids: string[]
  schedule_type: Campaign['schedule_type']
  scheduled_for: string   // local datetime input value (YYYY-MM-DDTHH:mm)
  recurring_cron: string
  recurring_dedup_days: number
  reply_to: string
  attribution_window_days: number
  mailgun_tags: string[]
  follow_up_waves: FormWave[]
}

const MAX_FOLLOW_UP_WAVES = 10

function emptyForm(): FormState {
  // Default one-shot schedule: now + 1 hour, rounded to next 5 min.
  const d = new Date(Date.now() + 60 * 60 * 1000)
  d.setSeconds(0, 0)
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5)
  const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  return {
    slug: '',
    name: '',
    template_id: '',
    segment_id: '',
    additional_segment_ids: [],
    schedule_type: 'one_shot',
    scheduled_for: localIso,
    recurring_cron: '0 14 * * *',
    recurring_dedup_days: 30,
    reply_to: '',
    attribution_window_days: 7,
    mailgun_tags: [],
    follow_up_waves: [],
  }
}

function pad(n: number): string {
  return n.toString().padStart(2, '0')
}

export function CampaignEditorPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = !id || id === 'new'
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const existing = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.get<Campaign>(`/api/campaigns/${id}`),
    enabled: !isNew,
    refetchInterval: 10_000,
  })

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<{ items: EmailTemplate[] }>('/api/templates'),
  })
  // Two fetches: the option list without member counts comes back in
  // well under a second; the counted list (the same one the Segments
  // page uses) takes ~30s for ~400 segments, so it fills the numbers
  // in after the picker is already usable.
  const segmentsLite = useQuery({
    queryKey: ['segments', 'lite'],
    queryFn: () => api.get<{ items: Segment[] }>('/api/segments?counts=0'),
  })
  const segments = useQuery({
    queryKey: ['segments'],
    queryFn: () => api.get<{ items: Segment[] }>('/api/segments'),
  })

  const [form, setForm] = useState<FormState>(emptyForm())
  // Hydrate the form from server state once per campaign id. The `existing`
  // query auto-refetches every 10s (to keep the Results panel live), so
  // re-running this on every `existing.data` change would clobber whatever
  // the operator just typed — that was the 2026-04-21 "scheduled time
  // doesn't save" bug. Track the id we last hydrated against to fire exactly
  // once per load/navigation.
  const hydratedIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!existing.data) return
    if (hydratedIdRef.current === id) return
    const e = existing.data
    setForm({
      slug: e.slug,
      name: e.name,
      template_id: e.template_id.toString(),
      segment_id: e.segment_id.toString(),
      additional_segment_ids: (e.additional_segment_ids ?? []).map(String),
      schedule_type: e.schedule_type,
      // datetime-local inputs expect LOCAL time in YYYY-MM-DDTHH:MM (no tz).
      // `toISOString()` emits UTC, which displays as the wrong wall-clock time
      // in local zones — that was the 'shows 4:40 PM after saving 12:40 PM'
      // bug (a 4h EDT offset).
      scheduled_for: e.scheduled_for ? (() => {
        const d = new Date(e.scheduled_for)
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
      })() : '',
      recurring_cron: e.recurring_cron ?? '0 14 * * *',
      recurring_dedup_days: e.recurring_dedup_days,
      reply_to: e.reply_to ?? '',
      attribution_window_days: e.attribution_window_days ?? 7,
      mailgun_tags: e.mailgun_tags ?? [],
      follow_up_waves: (e.follow_up_waves ?? []).map((w) => ({
        day_offset: String(w.day_offset),
        template_id: w.template_id ? String(w.template_id) : '',
        subject: w.subject ?? '',
      })),
    })
    hydratedIdRef.current = id ?? null
  }, [existing.data, id])

  const pickedTemplate = useMemo(() => {
    const tid = Number(form.template_id)
    return templates.data?.items.find((t) => t.id === tid)
  }, [form.template_id, templates.data])

  // No publication-scoped filter — a single template can be the
  // body of a campaign that targets segments across multiple titles
  // (e.g., a generic renewal-prompt template sent to AR Off Email
  // cohorts of every publication). Operator picks any combination.
  const segmentOptions = useMemo(() => {
    const counted = new Map<number, number>()
    for (const c of segments.data?.items ?? []) {
      if (c.member_count !== undefined) counted.set(c.id, c.member_count)
    }
    return (segmentsLite.data?.items ?? []).map((s) => ({
      ...s,
      member_count: counted.get(s.id) ?? s.member_count,
    }))
  }, [segmentsLite.data, segments.data])

  const body = useMemo(() => ({
    slug: form.slug,
    name: form.name,
    template_id: Number(form.template_id) || 0,
    segment_id: Number(form.segment_id) || 0,
    additional_segment_ids: form.additional_segment_ids
      .map((s) => Number(s))
      .filter((n) => !!n && n !== Number(form.segment_id)),
    schedule_type: form.schedule_type,
    scheduled_for: form.schedule_type === 'one_shot' && form.scheduled_for
      ? new Date(form.scheduled_for).toISOString()
      : null,
    recurring_cron: form.schedule_type === 'recurring' ? form.recurring_cron : null,
    recurring_dedup_days: form.recurring_dedup_days,
    reply_to: form.reply_to || null,
    attribution_window_days: form.attribution_window_days,
    mailgun_tags: form.mailgun_tags,
    follow_up_waves: form.schedule_type === 'one_shot'
      ? form.follow_up_waves
          .map((w) => ({
            day_offset: Number(w.day_offset) || 0,
            template_id: w.template_id ? Number(w.template_id) : null,
            subject: w.subject.trim() || null,
          }))
          .filter((w) => w.day_offset > 0)
      : [],
  }), [form])

  const followUpWavesError = useMemo<string | null>(() => {
    if (form.schedule_type !== 'one_shot') return null
    const offsets = form.follow_up_waves.map((w) => Number(w.day_offset))
    if (offsets.some((n) => !n || n < 1)) {
      return 'Each wave needs a positive day offset.'
    }
    for (let i = 1; i < offsets.length; i++) {
      if (offsets[i] <= offsets[i - 1]) {
        return 'Wave day offsets must be strictly increasing.'
      }
    }
    if (form.follow_up_waves.length > MAX_FOLLOW_UP_WAVES) {
      return `Max ${MAX_FOLLOW_UP_WAVES} follow-up waves.`
    }
    return null
  }, [form.schedule_type, form.follow_up_waves])

  const save = useMutation({
    mutationFn: async () => {
      if (isNew) return api.post<Campaign>('/api/campaigns', body)
      return api.put<Campaign>(`/api/campaigns/${id}`, body)
    },
    onSuccess: (c) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['campaign', String(c.id)] })
      if (isNew) navigate(`/campaigns/${c.id}`, { replace: true })
    },
  })

  const estimate = useQuery({
    queryKey: [
      'campaign-estimate', id, form.template_id, form.segment_id,
      form.additional_segment_ids.join(','),
    ],
    queryFn: () => api.post<CampaignEstimate>(`/api/campaigns/${id}/estimate`),
    enabled: !isNew && !!form.template_id && !!form.segment_id,
  })

  // Pick-a-subscriber for preview / test-send
  const [subscriberQuery, setSubscriberQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery(subscriberQuery), 300)
    return () => clearTimeout(h)
  }, [subscriberQuery])
  const subscriberList = useQuery({
    queryKey: ['campaign-samples', id, debouncedQuery],
    queryFn: () => api.get<{ items: Array<{ id: number; email: string; first_name: string | null; last_name: string | null; simplecirc_account_id: string }>; total: number; all_shown: boolean }>(
      `/api/campaigns/${id}/sample-subscribers?limit=1000&q=${encodeURIComponent(debouncedQuery)}`,
    ),
    enabled: !isNew && !!existing.data && !!existing.data.template_id,
  })
  const [pickedSubscriberId, setPickedSubscriberId] = useState<number | null>(null)

  const subscriberPreview = useQuery({
    queryKey: ['campaign-preview-subscriber', id, pickedSubscriberId],
    queryFn: () =>
      api.post<{
        to: string
        from_email: string
        subject: string
        html: string
        text: string | null
      }>(`/api/campaigns/${id}/preview-subscriber`, { subscriber_id: pickedSubscriberId }),
    enabled: !isNew && pickedSubscriberId !== null,
  })

  // Test send modal — defaults override-email to the logged-in user's email,
  // remembers the last value so subsequent opens don't re-type.
  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/me'),
    staleTime: 60_000,
  })
  const [testOpen, setTestOpen] = useState(false)
  const [testTarget, setTestTarget] = useState<'subscriber' | 'override' | 'pool'>('override')
  const [overrideEmail, setOverrideEmail] = useState('')
  const [personalizationEmail, setPersonalizationEmail] = useState('')
  const [selectedPoolId, setSelectedPoolId] = useState<number | null>(null)
  const testPools = useQuery({
    queryKey: ['test-pools'],
    queryFn: () => api.get<{ items: import('../api/client').TestPool[] }>('/api/test-pools'),
    enabled: testOpen,
  })
  useEffect(() => {
    if (overrideEmail) return
    const remembered = typeof window !== 'undefined' ? window.localStorage.getItem(TEST_EMAIL_LS_KEY) : null
    setOverrideEmail(remembered || me.data?.email || '')
  }, [me.data, overrideEmail])
  const testSend = useMutation({
    mutationFn: () => {
      const typedPersonalizationEmail = personalizationEmail.trim()
      if (typeof window !== 'undefined' && testTarget === 'override' && overrideEmail) {
        window.localStorage.setItem(TEST_EMAIL_LS_KEY, overrideEmail)
      }
      return api.post<{
        ok: boolean
        mode?: string
        mailgun_message_id?: string
        to_email?: string
        sent_count?: number
        failed_count?: number
        requested?: number
        test_pool_name?: string
        personalized_as?: string | null
        personalized_links?: boolean
        results?: Array<{ to_email: string; ok: boolean; error?: string }>
      }>(
        `/api/campaigns/${id}/test-send`,
        {
          subscriber_id:
            testTarget === 'override' && typedPersonalizationEmail
              ? undefined
              : pickedSubscriberId ?? undefined,
          personalization_email:
            testTarget === 'override' && typedPersonalizationEmail
              ? typedPersonalizationEmail
              : undefined,
          override_email:
            testTarget === 'override' ? overrideEmail : undefined,
          test_pool_id:
            testTarget === 'pool' ? selectedPoolId : undefined,
        },
      )
    },
  })

  const schedule = useMutation({
    mutationFn: (confirmToken: string) =>
      api.post<Campaign>(`/api/campaigns/${id}/schedule`, { confirm_token: confirmToken }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['campaign', id] })
      setConfirmOpen(false)
      setConfirmInput('')
    },
  })
  const cancel = useMutation({
    mutationFn: () => api.post<Campaign>(`/api/campaigns/${id}/cancel`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      queryClient.invalidateQueries({ queryKey: ['campaign', id] })
    },
  })
  const cloneCampaign = useMutation({
    mutationFn: () => {
      const baseSlug = existing.data?.slug ?? form.slug
      const baseName = existing.data?.name ?? form.name
      const ts = new Date().toISOString().slice(5, 16).replace(/[-T:]/g, '')
      return api.post<Campaign>(`/api/campaigns/${id}/clone`, {
        slug: `${baseSlug}-clone-${ts}`,
        name: `${baseName} (clone)`,
      })
    },
    onSuccess: (c) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      navigate(`/campaigns/${c.id}`)
    },
  })
  const deleteDraft = useMutation({
    mutationFn: () => api.del<{ ok: boolean; id: number }>(`/api/campaigns/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      navigate('/campaigns')
    },
  })

  // Only drafts can be scheduled. Cancelled is terminal — the user clones to
  // re-run, which avoids re-opening a previously-cancelled campaign (the root
  // cause of the 2026-04-20 accidental fire). Editability follows the same
  // rule since editing a cancelled campaign implies re-sending it.
  const isDraft = !existing.data || existing.data.status === 'draft'
  const isClonable = !isNew && existing.data && existing.data.status !== 'draft' && existing.data.status !== 'running'

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmInput, setConfirmInput] = useState('')

  // Pre-flight: Mailgun validation of the segment's recipient list. Auto-runs
  // when the confirm modal opens. Cold addresses get bulk-validated, cached
  // results return immediately. The SEND button is disabled until this
  // completes and the list passes health thresholds.
  const validation = useMutation({
    mutationFn: () =>
      api.post<SegmentValidationReport>(`/api/campaigns/${id}/validate-segment`),
  })
  // Template variable coverage — ensures every variable the template
  // references (renewal_link, first_name, etc.) resolves to a non-empty
  // value for ≥95% of recipients. Catches silent-null bugs like the
  // 2026-04-21 renewal_link incident before they ship.
  const tmplCoverage = useMutation({
    mutationFn: () =>
      api.post<TemplateCoverageReport>(`/api/campaigns/${id}/template-coverage`),
  })
  useEffect(() => {
    if (!confirmOpen || isNew) return
    // Trigger both checks once per modal open (not on re-renders)
    if (!validation.isPending && !validation.data && !validation.isError) validation.mutate()
    if (!tmplCoverage.isPending && !tmplCoverage.data && !tmplCoverage.isError) tmplCoverage.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmOpen])

  const netSendable = validation.data?.net_sendable
  const expectedToken = netSendable !== undefined
    ? `SEND ${netSendable}`
    : null
  const confirmMatches = confirmInput.trim() === expectedToken
  const validationPasses = validation.data && (
    validation.data.coverage >= 0.9
    && validation.data.undeliverable_rate <= 0.05
  )
  const tmplPasses = tmplCoverage.data?.passes ?? false
  const allChecksPass = validationPasses && tmplPasses

  const saveError = describeError(save.error)
  const schedError = describeError(schedule.error)

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3">
        <Link to="/campaigns" className="label-amber hover:underline">← Campaigns</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">{isNew ? 'New' : `Campaign #${id}`}</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <h1 className="font-serif text-[32px] leading-tight text-paper">
          {isNew ? 'New campaign' : form.name || form.slug}
        </h1>
        {!isNew && existing.data && (
          <div className="flex items-center gap-2">
            <StatusDot tone={existing.data.status === 'running' ? 'warn' : existing.data.status === 'scheduled' ? 'active' : existing.data.status === 'completed' ? 'ok' : existing.data.status === 'failed' ? 'err' : 'muted'} />
            <span className="label">{existing.data.status}</span>
          </div>
        )}
      </header>

      {saveError && (
        <div className="border border-radar-red/60 bg-radar-red/10 text-radar-red font-mono text-[12px] p-3">
          {saveError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-6">
        <div className="space-y-4">
          <Panel label="1 · Metadata" accent="amber">
            <div className="space-y-3">
              <Field
                label="Slug"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
                placeholder="e.g. fj-renewal-apr-2026"
                disabled={!isDraft}
              />
              <Field
                label="Name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Human-readable label"
                disabled={!isDraft}
              />
              <MailgunTagsEditor
                autoTag={existing.data ? `campaign-${existing.data.id}` : null}
                tags={form.mailgun_tags}
                onChange={(tags) => setForm((f) => ({ ...f, mailgun_tags: tags }))}
                disabled={!isDraft}
              />
            </div>
          </Panel>

          <Panel label="2 · Template">
            <StyledSingleSelect
              label="Template"
              value={form.template_id}
              onChange={(v) => setForm((f) => ({ ...f, template_id: v }))}
              placeholder="— select a template —"
              options={[
                { value: '', label: '— select a template —' },
                ...(templates.data?.items ?? [])
                  .filter((t) => t.enabled)
                  .map((t) => ({ value: t.id.toString(), label: `${t.name} (${t.slug})` })),
              ]}
              disabled={!isDraft}
            />
            {pickedTemplate && (
              <div className="font-mono text-[11px] text-paper-dim mt-2">
                Purpose: <span className="text-paper">{pickedTemplate.purpose}</span>
                {pickedTemplate.publication_id && (
                  <>
                    {' · '}Publication: <span className="text-paper">
                      {PUB_SLUG_BY_ID[pickedTemplate.publication_id] ?? `#${pickedTemplate.publication_id}`}
                    </span>
                  </>
                )}
              </div>
            )}
          </Panel>

          <Panel label="3 · Segments">
            <SegmentMultiSelect
              segments={segmentOptions}
              loading={segmentsLite.isPending}
              loadError={segmentsLite.isError ? describeError(segmentsLite.error) : null}
              onRetry={() => segmentsLite.refetch()}
              countsPending={segments.isPending}
              selectedIds={
                form.segment_id
                  ? [form.segment_id, ...form.additional_segment_ids]
                  : []
              }
              onChange={(ids) => {
                // First pick becomes segment_id (the primary stored
                // on the campaign row); the rest go into
                // additional_segment_ids. Order matters only as
                // implementation detail — the dispatcher unions them.
                const [first, ...rest] = ids
                setForm((f) => ({
                  ...f,
                  segment_id: first ?? '',
                  additional_segment_ids: rest,
                }))
              }}
              disabled={!isDraft}
            />
            <div className="font-mono text-[10px] text-paper-trace mt-3 leading-relaxed">
              Pick one or more segments. The campaign sends to the
              union of all selected — recipients are deduplicated so
              no one gets the email twice. Segments aren't filtered
              by title; a single template can target cohorts across
              multiple publications.
            </div>
          </Panel>

          <Panel label="4 · Schedule">
            <div className="space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={form.schedule_type === 'one_shot'}
                  onChange={() => setForm((f) => ({ ...f, schedule_type: 'one_shot' }))}
                  disabled={!isDraft}
                />
                <span className="label">One-shot</span>
              </label>
              {form.schedule_type === 'one_shot' && (
                <Field
                  label="Fire at (local time — stored as UTC)"
                  type="datetime-local"
                  value={form.scheduled_for}
                  onChange={(e) => setForm((f) => ({ ...f, scheduled_for: e.target.value }))}
                  disabled={!isDraft}
                />
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={form.schedule_type === 'recurring'}
                  onChange={() => setForm((f) => ({ ...f, schedule_type: 'recurring' }))}
                  disabled={!isDraft}
                />
                <span className="label">Recurring (Phase 2)</span>
              </label>
              {form.schedule_type === 'recurring' && (
                <>
                  <Field
                    label="Cron (5-field UTC)"
                    value={form.recurring_cron}
                    onChange={(e) => setForm((f) => ({ ...f, recurring_cron: e.target.value }))}
                    placeholder="0 14 * * *  (daily 14:00 UTC)"
                    disabled={!isDraft}
                  />
                  <Field
                    label="Dedup days"
                    type="number"
                    value={String(form.recurring_dedup_days)}
                    onChange={(e) => setForm((f) => ({ ...f, recurring_dedup_days: Number(e.target.value) || 0 }))}
                    disabled={!isDraft}
                  />
                </>
              )}
              <Field
                label="Reply-to (optional)"
                type="email"
                value={form.reply_to}
                onChange={(e) => setForm((f) => ({ ...f, reply_to: e.target.value }))}
                placeholder="editor@example.com"
                disabled={!isDraft}
              />
              <Field
                label="Attribution window (days)"
                type="number"
                value={String(form.attribution_window_days)}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  attribution_window_days: Math.max(1, Math.min(365, Number(e.target.value) || 7)),
                }))}
                placeholder="7"
                disabled={!isDraft}
              />
            </div>
          </Panel>

          <Panel label="5 · Follow-up waves">
            <FollowUpWavesEditor
              waves={form.follow_up_waves}
              onChange={(waves) => setForm((f) => ({ ...f, follow_up_waves: waves }))}
              templates={templates.data?.items ?? []}
              disabled={!isDraft || form.schedule_type !== 'one_shot'}
              recurringHint={form.schedule_type === 'recurring'}
            />
          </Panel>

          <div className="flex items-center gap-3">
            {isDraft && (
              <Button
                size="md"
                disabled={save.isPending || followUpWavesError !== null}
                onClick={() => save.mutate()}
              >
                {save.isPending ? 'Saving…' : isNew ? 'Create draft' : 'Save draft'}
              </Button>
            )}
            {!isNew && existing.data && isDraft && (
              <Button
                size="md"
                disabled={schedule.isPending || !estimate.data}
                onClick={() => {
                  setConfirmInput('')
                  setConfirmOpen(true)
                }}
              >
                {schedule.isPending ? 'Scheduling…' : 'Schedule…'}
              </Button>
            )}
            {!isNew && existing.data?.status === 'scheduled' && (
              <Button variant="outline" size="md" onClick={() => cancel.mutate()}>
                Cancel scheduled
              </Button>
            )}
            {isClonable && (
              <Button
                variant="outline"
                size="md"
                disabled={cloneCampaign.isPending}
                onClick={() => cloneCampaign.mutate()}
              >
                {cloneCampaign.isPending ? 'Cloning…' : 'Clone to new draft'}
              </Button>
            )}
            {!isNew && existing.data && isDraft && (
              <Button
                variant="outline"
                size="md"
                disabled={deleteDraft.isPending}
                onClick={() => {
                  if (window.confirm(`Delete draft "${existing.data?.name || existing.data?.slug}"? This cannot be undone.`)) {
                    deleteDraft.mutate()
                  }
                }}
                className="ml-auto text-radar-red border-radar-red/60 hover:bg-radar-red/10"
              >
                {deleteDraft.isPending ? 'Deleting…' : 'Delete draft'}
              </Button>
            )}
          </div>
          {schedError && (
            <div className="font-mono text-[12px] text-radar-red">{schedError}</div>
          )}
        </div>

        <div className="space-y-4">
          <Panel label="Review" accent="cyan">
            {!estimate.data ? (
              <div className="label text-paper-trace p-6">Save the draft to see the recipient estimate.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="label text-paper-faint">Recipients</span>
                  <span className="font-mono text-[28px] font-semibold text-amber-glow tabular-nums">
                    {fmtInt(estimate.data.recipient_count)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="label text-paper-faint">From</span>
                  <span className="font-mono text-[12px] text-paper">{estimate.data.from_email}</span>
                </div>
              </div>
            )}
          </Panel>

          <Panel label="Preview as subscriber" accent="amber">
            {isNew ? (
              <div className="label text-paper-trace p-6">Save the draft to pick a subscriber.</div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTestOpen(true)}
                  >
                    Send test →
                  </Button>
                </div>
                <div>
                  <Field
                    label="Search this segment"
                    value={subscriberQuery}
                    onChange={(e) => setSubscriberQuery(e.target.value)}
                    placeholder="email, first or last name"
                  />
                </div>
                {subscriberList.data && (
                  <div className="font-mono text-[10px] text-paper-dim">
                    {fmtInt(subscriberList.data.total)} {subscriberQuery ? 'matching' : 'cohort'} subscriber{subscriberList.data.total === 1 ? '' : 's'}
                    {!subscriberList.data.all_shown ? ' · refine your search to see the rest' : ' · all available below'}
                  </div>
                )}
                <div className="max-h-[200px] overflow-y-auto border border-ink-500 bg-ink-700/40">
                  {subscriberList.isLoading && (
                    <div className="p-3 label text-paper-trace">Searching…</div>
                  )}
                  {subscriberList.data && subscriberList.data.items.length === 0 && (
                    <div className="p-3 label text-paper-trace">No matching subscribers.</div>
                  )}
                  {subscriberList.data?.items.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setPickedSubscriberId(s.id)}
                      className={`w-full text-left px-3 py-2 flex items-baseline justify-between gap-3 border-b border-ink-500/30 hover:bg-ink-700/60 ${
                        pickedSubscriberId === s.id ? 'bg-amber-glow/20 text-amber-glow' : 'text-paper'
                      }`}
                    >
                      <span className="font-mono text-[11px] truncate">{s.email}</span>
                      <span className="font-mono text-[10px] text-paper-dim shrink-0">
                        {[s.first_name, s.last_name].filter(Boolean).join(' ') || '—'}
                      </span>
                    </button>
                  ))}
                </div>

                {pickedSubscriberId === null && (
                  <div className="label text-paper-trace">
                    Pick a subscriber above to see a personalized preview.
                  </div>
                )}

                {subscriberPreview.data && (
                  <div className="space-y-2 pt-1">
                    <div className="font-mono text-[11px]">
                      <span className="text-paper-faint">to </span>
                      <span className="text-paper">{subscriberPreview.data.to}</span>
                    </div>
                    <div>
                      <div className="label text-paper-faint">Subject</div>
                      <div className="font-mono text-[12px] text-paper">{subscriberPreview.data.subject}</div>
                    </div>
                    <div>
                      <div className="label text-paper-faint mb-1">HTML</div>
                      {/* No allow-* flags: preview HTML can't run in the
                          console's origin (security review 2026-09-30). */}
                      <iframe
                        sandbox=""
                        srcDoc={subscriberPreview.data.html}
                        className="w-full h-[500px] border border-ink-500 bg-white"
                        title="Preview"
                      />
                    </div>
                  </div>
                )}
                {subscriberPreview.isLoading && pickedSubscriberId !== null && (
                  <div className="label text-paper-trace p-4">Rendering…</div>
                )}
                {subscriberPreview.error && (
                  <div className="font-mono text-[11px] text-radar-red">
                    Preview error: {describeError(subscriberPreview.error)}
                  </div>
                )}
              </div>
            )}
          </Panel>

          {confirmOpen && (
            <div
              className="fixed inset-0 bg-ink-900/80 flex items-center justify-center z-50 p-6"
              onClick={() => !schedule.isPending && setConfirmOpen(false)}
            >
              <div
                className="bg-ink-800 border border-radar-red/60 w-full max-w-[560px] p-6 space-y-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="label-amber text-radar-red">
                  Confirm scheduled send
                </div>
                <div className="font-mono text-[12px] text-paper space-y-1">
                  <div>
                    Segment:{' '}
                    <span className="text-amber-glow">
                      {segmentOptions.find((s) => String(s.id) === form.segment_id)?.slug ?? '—'}
                    </span>
                    {form.additional_segment_ids.length > 0 && (
                      <span className="text-paper-dim">
                        {' '}+ {form.additional_segment_ids.length} additional segment{form.additional_segment_ids.length === 1 ? '' : 's'}
                      </span>
                    )}
                  </div>
                  <div>
                    From: <span className="text-paper-dim">{estimate.data?.from_email ?? '—'}</span>
                  </div>
                  <div>
                    Fires at: <span className="text-paper-dim">{form.scheduled_for || '—'}</span>
                  </div>
                  {form.schedule_type === 'one_shot' && form.follow_up_waves.length > 0 && (
                    <div>
                      Follow-ups to non-clickers:{' '}
                      <span className="text-amber-glow">
                        {form.follow_up_waves
                          .map((w) => `+${w.day_offset || '?'}d`)
                          .join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                <ValidationHealthCard
                  isPending={validation.isPending}
                  isError={validation.isError}
                  error={validation.error}
                  data={validation.data ?? null}
                  onRetry={() => validation.mutate()}
                />

                <TemplateCoveragePanel
                  isPending={tmplCoverage.isPending}
                  isError={tmplCoverage.isError}
                  error={tmplCoverage.error}
                  data={tmplCoverage.data ?? null}
                  onRetry={() => tmplCoverage.mutate()}
                />

                <div className="border-t border-ink-500 pt-4 space-y-2">
                  <div className="font-mono text-[11px] text-paper-dim">
                    Type the phrase below <span className="text-paper">exactly</span> to unlock
                    the schedule button. This is a deliberate friction step
                    added after the 2026-04-20 incident where a cancelled
                    campaign was accidentally re-opened and fired.
                  </div>
                  <div className="font-mono text-[14px] text-radar-red bg-ink-900 border border-ink-500 p-2 select-all">
                    {expectedToken ?? '…'}
                  </div>
                  <Field
                    value={confirmInput}
                    onChange={(e) => setConfirmInput(e.target.value)}
                    placeholder={allChecksPass ? 'type the phrase above' : 'resolve checks above first'}
                    autoFocus
                    disabled={schedule.isPending || !allChecksPass}
                  />
                </div>
                {schedError && (
                  <div className="font-mono text-[11px] text-radar-red">
                    {schedError}
                  </div>
                )}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={schedule.isPending}
                    onClick={() => setConfirmOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!confirmMatches || schedule.isPending || !expectedToken || !allChecksPass}
                    onClick={() => expectedToken && schedule.mutate(expectedToken)}
                  >
                    {schedule.isPending ? 'Scheduling…' : `Schedule — send to ${netSendable ?? 0}`}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {testOpen && (
            <div
              className="fixed inset-0 bg-ink-900/80 flex items-center justify-center z-50 p-6"
              onClick={() => setTestOpen(false)}
            >
              <div
                className="bg-ink-800 border border-ink-500 w-full max-w-[520px] p-6 space-y-4"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="label-amber">Send test email</div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={testTarget === 'override'}
                      onChange={() => setTestTarget('override')}
                    />
                    <span className="label">Custom test</span>
                  </label>
                  {testTarget === 'override' && (
                    <div className="pl-6 space-y-3">
                      <div>
                        <Field
                          label="Personalize as member"
                          type="email"
                          value={personalizationEmail}
                          onChange={(e) => setPersonalizationEmail(e.target.value)}
                          placeholder="member@example.com"
                        />
                        <div className="font-mono text-[10px] text-paper-dim mt-1">
                          Enter any member email, including someone outside this campaign cohort.
                          Leave blank to use the picked cohort member or sample data.
                        </div>
                      </div>
                      <Field
                        label="Send test to"
                        type="email"
                        value={overrideEmail}
                        onChange={(e) => setOverrideEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                  )}
                  <label
                    className={`flex items-center gap-2 ${subscriberPreview.data ? 'cursor-pointer' : 'opacity-50'}`}
                  >
                    <input
                      type="radio"
                      checked={testTarget === 'subscriber'}
                      onChange={() => setTestTarget('subscriber')}
                      disabled={!subscriberPreview.data}
                    />
                    <span className="label">
                      Actually send to picked subscriber
                      {subscriberPreview.data && (
                        <span className="ml-2 font-mono text-[10px] text-paper-dim">
                          ({subscriberPreview.data.to})
                        </span>
                      )}
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={testTarget === 'pool'}
                      onChange={() => setTestTarget('pool')}
                    />
                    <span className="label">
                      Test pool — send to every member of a pre-vetted list
                    </span>
                  </label>
                  {testTarget === 'pool' && (
                    <div className="pl-6 space-y-1.5">
                      <select
                        value={selectedPoolId ?? ''}
                        onChange={(e) => {
                          const v = e.target.value
                          setSelectedPoolId(v ? Number(v) : null)
                        }}
                        className="bg-ink-900 border border-ink-500 px-3 py-2 font-mono text-[13px] text-paper focus:outline-none focus:border-amber-glow min-w-[280px]"
                      >
                        <option value="">— pick a test pool —</option>
                        {testPools.data?.items.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.member_count} {p.member_count === 1 ? 'member' : 'members'})
                          </option>
                        ))}
                      </select>
                      {testPools.data && testPools.data.items.length === 0 && (
                        <div className="font-mono text-[10px] text-paper-dim">
                          No test pools yet. <Link to="/subscribers/test-pools" className="label-amber hover:underline">Create one →</Link>
                        </div>
                      )}
                      <div className="font-mono text-[10px] text-paper-dim">
                        {subscriberPreview.data
                          ? `Every pool recipient will receive the version personalized as ${subscriberPreview.data.to}, including that member's links.`
                          : 'No cohort member is selected, so each pool recipient will receive their own personalization.'}
                      </div>
                      <div className="font-mono text-[10px] text-radar-red/80">
                        ⚠ This is a REAL send. Pool members receive the email and Mailgun
                        bills the messages.
                      </div>
                    </div>
                  )}
                </div>
                <div className="font-mono text-[10px] text-paper-dim">
                  Test sends are tagged <span className="text-paper">
                    campaign-{id}-{testTarget === 'pool' ? 'pooltest' : 'test'}
                  </span> in Mailgun and are NOT recorded in campaign history.
                </div>
                {testSend.error && (
                  <div className="font-mono text-[11px] text-radar-red">
                    {describeError(testSend.error)}
                  </div>
                )}
                {testSend.isSuccess && testSend.data?.mode === 'test_pool' && (
                  <div className="font-mono text-[11px] text-radar-green space-y-1">
                    <div>
                      ✓ sent to {testSend.data?.sent_count ?? 0} of {testSend.data?.requested ?? 0} pool members
                      {testSend.data?.test_pool_name && (
                        <span className="text-paper-dim ml-2">· pool: {testSend.data.test_pool_name}</span>
                      )}
                      {testSend.data?.failed_count ? (
                        <span className="text-radar-red ml-2">
                          · {testSend.data.failed_count} failed
                        </span>
                      ) : null}
                    </div>
                    {testSend.data?.personalized_as && (
                      <div className="text-paper-dim">
                        Rendered as {testSend.data.personalized_as} with that member's personalized links
                      </div>
                    )}
                    <details className="cursor-pointer">
                      <summary className="text-paper-dim hover:text-paper">
                        Show recipients
                      </summary>
                      <ul className="mt-1 space-y-0.5 pl-4">
                        {testSend.data.results?.map((r) => (
                          <li key={r.to_email} className={r.ok ? 'text-paper-dim' : 'text-radar-red'}>
                            {r.ok ? '✓' : '✗'} {r.to_email}
                            {r.error && <span className="ml-2 text-radar-red/80">({r.error})</span>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                )}
                {testSend.isSuccess && testSend.data?.mode !== 'test_pool' && (
                  <div className="font-mono text-[11px] text-radar-green space-y-1">
                    <div>✓ sent to {testSend.data?.to_email} · Mailgun id {testSend.data?.mailgun_message_id}</div>
                    {testSend.data?.personalized_as && (
                      <div className="text-paper-dim">
                        Rendered as {testSend.data.personalized_as} with personalized member links
                      </div>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-end gap-3 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setTestOpen(false)}>
                    Close
                  </Button>
                  <Button
                    size="sm"
                    disabled={
                      testSend.isPending ||
                      (testTarget === 'override' && (
                        !overrideEmail.includes('@') ||
                        (Boolean(personalizationEmail.trim()) && !personalizationEmail.includes('@'))
                      )) ||
                      (testTarget === 'subscriber' && !pickedSubscriberId) ||
                      (testTarget === 'pool' && !selectedPoolId)
                    }
                    onClick={() => testSend.mutate()}
                  >
                    {(() => {
                      if (testSend.isPending) return 'Sending…'
                      if (testTarget === 'pool' && selectedPoolId) {
                        const p = testPools.data?.items.find((x) => x.id === selectedPoolId)
                        if (p) return `Send to ${p.member_count}`
                      }
                      return 'Send'
                    })()}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {!isNew && existing.data && (
            <Panel label="Run history">
              <div className="space-y-2 font-mono text-[11px]">
                {existing.data.last_fired_at && (
                  <div>
                    Last fired:{' '}
                    <span className="text-paper">{fmtRelative(existing.data.last_fired_at)}</span>
                    {' · sent '}
                    <span className="text-amber-glow tabular-nums">{fmtInt(existing.data.last_run_sent_count)}</span>
                  </div>
                )}
                {existing.data.next_fire_at && (
                  <div>
                    Next fire:{' '}
                    <span className="text-radar-cyan">{fmtRelative(existing.data.next_fire_at)}</span>
                  </div>
                )}
                {existing.data.last_run_error && (
                  <div className="text-radar-red">Last error: {existing.data.last_run_error}</div>
                )}
              </div>
            </Panel>
          )}

          {!isNew && existing.data && (existing.data.sends_total ?? 0) > 0 && (
            <Panel
              label="Results"
              accent="cyan"
              right={
                <Link to={`/campaigns/${id}/results`} className="label-amber hover:underline">
                  View full results →
                </Link>
              }
            >
              <ResultsSummary
                total={existing.data.sends_total ?? 0}
                breakdown={existing.data.status_breakdown ?? {}}
              />
            </Panel>
          )}
        </div>
      </div>
    </div>
  )
}

const PUB_SLUG_BY_ID: Record<number, string> = {
  1: 'rc_car_action',
  2: 'model_airplane_news',
  3: 'flight_journal',
  4: 'diecast',
}

// Compact at-a-glance summary shown inline on the campaign editor. The full
// detail (per-recipient table, link clicks, conversions) lives on
// /campaigns/:id/results — this panel just teases the headline numbers so the
// editor stays focused on configuration.
function ResultsSummary({
  total, breakdown,
}: {
  total: number
  breakdown: Record<string, number>
}) {
  // `breakdown` rows are mutually-exclusive terminal states. A message that
  // was opened lives in the `opened` bucket only — it is *also* delivered.
  // Render the funnel as cumulative counts (delivered = delivered+opened+
  // clicked, opened = opened+clicked) so the tile matches the full results
  // page and email-industry conventions.
  const delivered = (breakdown.delivered ?? 0) + (breakdown.opened ?? 0) + (breakdown.clicked ?? 0)
  const opened = (breakdown.opened ?? 0) + (breakdown.clicked ?? 0)
  const clicked = (breakdown.clicked ?? 0)
  const failed = (breakdown.failed ?? 0)
  const deliveryRate = total > 0 ? Math.round((delivered / total) * 100) : null

  const tiles: Array<{ key: string; label: string; count: number; tone: string }> = [
    { key: 'delivered', label: 'delivered', count: delivered, tone: 'text-paper' },
    { key: 'opened',    label: 'opened',    count: opened,    tone: 'text-radar-green' },
    { key: 'clicked',   label: 'clicked',   count: clicked,   tone: 'text-radar-cyan' },
    { key: 'failed',    label: 'failed',    count: failed,    tone: 'text-radar-red' },
  ]

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between border-b border-ink-500 pb-3">
        <div>
          <div className="label text-paper-faint">Total sends</div>
          <div className="font-mono text-[26px] font-semibold text-amber-glow tabular-nums">
            {fmtInt(total)}
          </div>
        </div>
        {deliveryRate !== null && (
          <div className="text-right">
            <div className="label text-paper-faint">Delivery</div>
            <div className="font-mono text-[20px] text-radar-green tabular-nums">
              {deliveryRate}%
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {tiles.map((t) => {
          const pct = total > 0 ? (t.count / total) * 100 : 0
          return (
            <div key={t.key} className="border border-ink-500 px-3 py-2">
              <div className={`label ${t.tone}`}>{t.label}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-[18px] tabular-nums text-paper">
                  {fmtInt(t.count)}
                </span>
                <span className="font-mono text-[10px] tabular-nums text-paper-trace">
                  {pct >= 10 ? pct.toFixed(0) : pct.toFixed(1)}%
                </span>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/**
 * Multi-select segment picker. Trigger styled exactly like the
 * existing Select component (same uppercase / ink-700 chrome).
 * Click opens a dropdown with a checkbox row per segment; multiple
 * picks in one open session, click outside or press Esc to close.
 * Selected segments render below as removable chips.
 */
/**
 * Single-select dropdown with the same trigger + portal-rendered
 * dropdown styling as SegmentMultiSelect. Used for the Template
 * picker so it visually matches the Segment picker.
 */
function StyledSingleSelect({
  label, value, onChange, options, disabled, placeholder,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string; sublabel?: string }>
  disabled: boolean
  placeholder?: string
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)

  useEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    function update() {
      const t = triggerRef.current
      if (!t) return
      const r = t.getBoundingClientRect()
      setPos({ top: r.bottom + 2, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      const t = triggerRef.current
      const d = dropdownRef.current
      const target = e.target as Node
      if (t && t.contains(target)) return
      if (d && d.contains(target)) return
      setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const selected = options.find((o) => o.value === value && o.value !== '')

  return (
    <div className="relative">
      <span className="label mb-1.5 block">{label}</span>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`
          w-full bg-ink-700 border border-ink-500
          font-mono text-[12px] uppercase tracking-label
          py-1.5 pl-3 pr-8 text-left relative
          focus:outline-none focus:border-amber-glow
          ${disabled ? 'opacity-50 cursor-not-allowed text-paper-trace' : 'cursor-pointer text-paper'}
        `}
      >
        {selected
          ? <span>{selected.label}</span>
          : <span className="text-paper-faint normal-case tracking-normal">{placeholder ?? '— select —'}</span>
        }
        <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-paper-faint pointer-events-none">
          ▾
        </span>
      </button>

      {open && !disabled && pos !== null && createPortal(
        <div
          ref={dropdownRef}
          className="border-2 border-amber-glow/60 max-h-[300px] overflow-y-auto font-mono text-[12px]"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            backgroundColor: '#2a313d',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
          }}
        >
          {options.filter((o) => o.value !== '').map((o) => {
            const checked = o.value === value
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => {
                  onChange(o.value)
                  setOpen(false)
                }}
                className={`w-full flex items-center gap-2 px-3 py-1.5 cursor-pointer border-b border-ink-600/60 last:border-b-0 text-left ${
                  checked ? 'text-amber-glow' : 'text-paper'
                }`}
                style={{
                  backgroundColor: checked ? 'rgba(255, 182, 72, 0.18)' : '#2a313d',
                }}
              >
                <span className="flex-1 truncate">{o.label}</span>
                {o.sublabel && (
                  <span className="text-paper-faint shrink-0">{o.sublabel}</span>
                )}
              </button>
            )
          })}
        </div>,
        document.body,
      )}
    </div>
  )
}

function SegmentMultiSelect({
  segments, selectedIds, onChange, disabled,
  loading = false, loadError = null, onRetry, countsPending = false,
}: {
  segments: Segment[]
  selectedIds: string[]
  onChange: (ids: string[]) => void
  disabled: boolean
  loading?: boolean
  loadError?: string | null
  onRetry?: () => void
  countsPending?: boolean
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)

  // Compute the dropdown position from the trigger's screen rect,
  // updating on scroll and resize so it stays anchored. Rendered via
  // portal at <body> so no parent stacking-context can clip it.
  useEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    function update() {
      const t = triggerRef.current
      if (!t) return
      const r = t.getBoundingClientRect()
      setPos({ top: r.bottom + 2, left: r.left, width: r.width })
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      const t = triggerRef.current
      const d = dropdownRef.current
      const target = e.target as Node
      if (t && t.contains(target)) return
      if (d && d.contains(target)) return
      setOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onEsc)
    }
  }, [open])

  const segmentById = useMemo(() => {
    const m = new Map<string, Segment>()
    for (const s of segments) m.set(s.id.toString(), s)
    return m
  }, [segments])

  const selectedSegments = selectedIds
    .map((id) => segmentById.get(id))
    .filter((s): s is Segment => !!s)

  // Sum of member counts across selected. May overstate the true
  // recipient pool when segments overlap (a sub in multiple
  // segments is counted once per segment); the post-save Estimate
  // panel below shows the true deduped count.
  const totalCount = selectedSegments.reduce(
    (a, s) => a + (s.member_count ?? 0), 0,
  )

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  return (
    <div className="relative">
      <span className="label mb-1.5 block">Segments</span>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        aria-busy={loading}
        onClick={() => setOpen((v) => !v)}
        className={`
          w-full bg-ink-700 border border-ink-500
          font-mono text-[12px] uppercase tracking-label
          py-1.5 pl-3 pr-8 text-left relative
          focus:outline-none focus:border-amber-glow
          ${disabled || loading ? 'opacity-50 cursor-not-allowed text-paper-trace' : 'cursor-pointer text-paper'}
        `}
      >
        {loading
          ? (
            <span className="text-paper-faint normal-case tracking-normal animate-pulse">
              Loading segments…
            </span>
          )
          : selectedSegments.length === 0
          ? <span className="text-paper-faint normal-case tracking-normal">— select segments —</span>
          : (
            <span>
              {selectedSegments.length} segment{selectedSegments.length === 1 ? '' : 's'} selected
              <span className="text-paper-faint ml-2">·</span>
              {countsPending
                ? (
                  <span className="text-paper-faint ml-2 normal-case tracking-normal animate-pulse">
                    counting members…
                  </span>
                )
                : (
                  <>
                    <span className="text-amber-glow ml-2 normal-case tabular-nums">
                      {fmtInt(totalCount)}
                    </span>
                    <span className="text-paper-faint ml-1 normal-case tracking-normal">total</span>
                  </>
                )}
            </span>
          )
        }
        <span className="absolute right-2 top-1/2 -translate-y-1/2 font-mono text-[10px] text-paper-faint pointer-events-none">
          {loading ? '…' : '▾'}
        </span>
      </button>
      {loadError && (
        <div className="mt-1.5 font-mono text-[11px] text-radar-red">
          Segments failed to load: {loadError}
          {onRetry && (
            <button type="button" onClick={onRetry} className="ml-2 underline text-amber-glow">
              retry
            </button>
          )}
        </div>
      )}

      {open && !disabled && pos !== null && createPortal(
        <div
          ref={dropdownRef}
          className="border-2 border-amber-glow/60 max-h-[300px] overflow-y-auto font-mono text-[12px]"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: pos.width,
            backgroundColor: '#2a313d',
            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
          }}
        >
          {segments.map((s) => {
            const idStr = s.id.toString()
            const checked = selectedIds.includes(idStr)
            return (
              <label
                key={s.id}
                className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer border-b border-ink-600/60 last:border-b-0 ${
                  checked ? 'text-amber-glow' : 'text-paper'
                }`}
                style={{
                  backgroundColor: checked ? 'rgba(255, 182, 72, 0.18)' : '#2a313d',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(idStr)}
                  className="shrink-0"
                />
                <span className="flex-1 truncate">{s.name}</span>
                {s.member_count !== undefined
                  ? (
                    <span className="text-paper-faint tabular-nums shrink-0">
                      {fmtInt(s.member_count)}
                    </span>
                  )
                  : countsPending && (
                    <span className="text-paper-trace shrink-0 animate-pulse">…</span>
                  )}
              </label>
            )
          })}
        </div>,
        document.body,
      )}

      {selectedSegments.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {selectedSegments.map((s) => (
            <span
              key={s.id}
              className="inline-flex items-center gap-1.5 bg-amber-glow/15 border border-amber-glow/40 px-2 py-0.5 font-mono text-[11px] text-amber-glow rounded-sm"
            >
              <span>{s.name}</span>
              {s.member_count !== undefined && (
                <span className="text-amber-glow/70">({fmtInt(s.member_count)})</span>
              )}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => toggle(s.id.toString())}
                  className="text-amber-glow/60 hover:text-amber-glow ml-0.5 leading-none text-[14px]"
                  aria-label={`Remove ${s.name}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

const TAG_RE = /^[A-Za-z0-9][A-Za-z0-9_\-]{0,127}$/

function MailgunTagsEditor({
  autoTag, tags, onChange, disabled,
}: {
  autoTag: string | null          // null on brand-new drafts (no id yet)
  tags: string[]
  onChange: (t: string[]) => void
  disabled: boolean
}) {
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const maxCustom = 2             // Mailgun caps at 3 total; auto tag = 1 slot.

  const addTag = () => {
    const t = input.trim()
    if (!t) return
    if (!TAG_RE.test(t)) {
      setError('letters/digits/dash/underscore only, start with letter or digit')
      return
    }
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) {
      setError('tag already added')
      return
    }
    if (tags.length >= maxCustom) {
      setError(`max ${maxCustom} custom tags (auto tag takes the third slot)`)
      return
    }
    onChange([...tags, t])
    setInput('')
    setError(null)
  }
  const removeTag = (t: string) => onChange(tags.filter((x) => x !== t))

  return (
    <div>
      <span className="label mb-1.5 block">Mailgun tags</span>
      <div className="flex flex-wrap items-center gap-2 min-h-[28px]">
        {autoTag && (
          <span
            className="inline-flex items-center gap-1 font-mono text-[11px] bg-ink-900 border border-amber-glow/40 text-amber-glow px-2 py-0.5 select-all"
            title="auto-attached, not removable"
          >
            🔒 {autoTag}
          </span>
        )}
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1 font-mono text-[11px] bg-ink-700 border border-ink-500 text-paper px-2 py-0.5"
          >
            {t}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(t)}
                className="text-paper-trace hover:text-radar-red"
                aria-label={`remove tag ${t}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        {!disabled && tags.length < maxCustom && (
          <input
            type="text"
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(null) }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addTag()
              } else if (e.key === ',') {
                e.preventDefault()
                addTag()
              } else if (e.key === 'Backspace' && input === '' && tags.length) {
                onChange(tags.slice(0, -1))
              }
            }}
            onBlur={() => input.trim() && addTag()}
            placeholder="add tag + Enter"
            className="bg-transparent border-b border-ink-500 font-mono text-[12px] text-paper py-0.5 focus:outline-none focus:border-amber-glow min-w-[140px]"
          />
        )}
      </div>
      {error && (
        <div className="font-mono text-[10px] text-radar-red mt-1">{error}</div>
      )}
      <div className="font-mono text-[10px] text-paper-trace mt-1">
        Every send auto-includes <code className="text-amber-glow">{autoTag ?? 'campaign-&lt;id&gt;'}</code>.
        Add up to 2 custom tags for cross-campaign analytics in Mailgun.
      </div>
    </div>
  )
}

function TemplateCoveragePanel({
  isPending, isError, error, data, onRetry,
}: {
  isPending: boolean
  isError: boolean
  error: unknown
  data: TemplateCoverageReport | null
  onRetry: () => void
}) {
  if (isPending) {
    return (
      <div className="border border-ink-500 bg-ink-900/40 p-3 space-y-2">
        <div className="label-amber">Template variable coverage</div>
        <div className="font-mono text-[12px] text-paper-trace">
          Checking every variable the template references resolves for this segment…
        </div>
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="border border-radar-red/60 bg-radar-red/10 p-3 space-y-2">
        <div className="label-amber text-radar-red">Template coverage · check failed</div>
        <div className="font-mono text-[11px] text-radar-red">
          {describeError(error) ?? 'Unknown error.'}
        </div>
        <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
      </div>
    )
  }

  const toneClass = (t: string) =>
    t === 'fail' ? 'text-radar-red'
      : t === 'warn' ? 'text-amber-glow'
      : t === 'unknown' ? 'text-paper-dim'
      : 'text-radar-green'

  return (
    <div className={`border p-3 space-y-2 ${data.passes
      ? 'border-radar-green/40 bg-radar-green/5'
      : 'border-radar-red/60 bg-radar-red/10'}`}
    >
      <div className="flex items-center justify-between">
        <span className="label-amber">Template variable coverage</span>
        <button
          className="label text-paper-faint hover:text-amber-glow"
          onClick={onRetry}
        >
          re-run
        </button>
      </div>

      {data.variables.length === 0 ? (
        <div className="font-mono text-[11px] text-paper-trace">
          Template references no merge variables — nothing to verify.
        </div>
      ) : (
        <div className="space-y-2">
          {data.variables.map((v) => {
            const pct = (v.coverage * 100).toFixed(1)
            const symbol = v.tone === 'fail' ? '✗'
              : v.tone === 'warn' ? '⚠'
              : v.tone === 'unknown' ? '?'
              : '✓'
            return (
              <div key={v.variable} className="font-mono text-[12px]">
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className={toneClass(v.tone)}>{symbol}</span>
                    <code className="text-paper">{v.variable}</code>
                    {!v.known_source && (
                      <span className="text-paper-trace text-[10px]">
                        (unknown source — not auto-verifiable)
                      </span>
                    )}
                  </span>
                  <span className="text-paper-dim tabular-nums whitespace-nowrap shrink-0">
                    {v.known_source ? (
                      <>
                        <span className={toneClass(v.tone)}>{pct}%</span>
                        {' '}
                        <span className="text-paper-trace">
                          ({fmtInt(v.populated)} / {fmtInt(v.total)})
                        </span>
                      </>
                    ) : '—'}
                  </span>
                </div>
                {v.has_default && (
                  <div className="pl-5 mt-0.5 flex items-baseline gap-1.5 text-[11px]">
                    <span className="text-radar-cyan">🛡 default:</span>
                    <span
                      className="text-paper truncate"
                      title={v.default_value ?? ''}
                    >
                      {v.default_value}
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {!data.passes && (
        <div className="font-mono text-[11px] text-radar-red border-t border-ink-500 pt-2">
          Hard block: coverage must be ≥ 95% for every known variable before
          this campaign can be scheduled.
          {data.hard_block_variables.length > 0 && (
            <span> Affected: <code>{data.hard_block_variables.join(', ')}</code>.</span>
          )}
        </div>
      )}
      {data.unknown_variables.length > 0 && (
        <div className="font-mono text-[10px] text-paper-trace">
          Variables not in the known-sources registry are shown but not gated.
          Preview the template against a real subscriber to visually confirm.
        </div>
      )}
    </div>
  )
}


function ValidationHealthCard({
  isPending, isError, error, data, onRetry,
}: {
  isPending: boolean
  isError: boolean
  error: unknown
  data: SegmentValidationReport | null
  onRetry: () => void
}) {
  if (isPending) {
    return (
      <div className="border border-ink-500 bg-ink-900/40 p-3 space-y-2">
        <div className="label-amber">List health · Mailgun validation</div>
        <div className="font-mono text-[12px] text-paper-trace">
          Validating addresses against Mailgun…
          <span className="inline-block animate-pulse ml-2">▮▮▮</span>
        </div>
        <div className="font-mono text-[10px] text-paper-faint">
          Uncached addresses are being bulk-validated (can take up to a minute for large segments).
        </div>
      </div>
    )
  }
  if (isError || !data) {
    return (
      <div className="border border-radar-red/60 bg-radar-red/10 p-3 space-y-2">
        <div className="label-amber text-radar-red">List health · validation failed</div>
        <div className="font-mono text-[11px] text-radar-red">
          {describeError(error) ?? 'Unknown error during validation.'}
        </div>
        <Button variant="outline" size="sm" onClick={onRetry}>Retry</Button>
      </div>
    )
  }

  const coveragePct = (data.coverage * 100).toFixed(0)
  const undeliverablePct = (data.undeliverable_rate * 100).toFixed(1)
  const coverageOk = data.coverage >= 0.9
  const rateOk = data.undeliverable_rate <= 0.05
  const passes = coverageOk && rateOk

  return (
    <div className={`border p-3 space-y-2 ${passes ? 'border-radar-green/40 bg-radar-green/5' : 'border-radar-red/60 bg-radar-red/10'}`}>
      <div className="flex items-center justify-between">
        <span className="label-amber">List health · Mailgun validation</span>
        <button
          className="label text-paper-faint hover:text-amber-glow"
          onClick={onRetry}
        >
          re-run
        </button>
      </div>

      <div className="grid grid-cols-4 gap-2 text-center">
        <HealthTile label="Deliverable" value={data.by_status['deliverable'] ?? 0} tone="text-radar-green" />
        <HealthTile label="Catch-all" value={data.by_status['catch_all'] ?? 0} tone="text-paper" />
        <HealthTile label="Undeliverable" value={data.undeliverable_count} tone="text-radar-red" />
        <HealthTile label="High risk" value={data.high_risk_count} tone="text-amber-glow" />
      </div>

      <div className="font-mono text-[11px] space-y-0.5">
        <div className={coverageOk ? 'text-paper-dim' : 'text-radar-red'}>
          Coverage: {coveragePct}% ({data.total - data.unvalidated_count} / {data.total} validated)
          {!coverageOk && ' · must be ≥90% to schedule'}
        </div>
        <div className={rateOk ? 'text-paper-dim' : 'text-radar-red'}>
          Undeliverable rate: {undeliverablePct}% ({data.undeliverable_count} / {data.total})
          {!rateOk && ' · must be ≤5% to schedule'}
        </div>
        <div className="text-paper-trace">
          Role addresses: {data.role_addresses} · Disposable: {data.disposable_addresses}
          {data.did_you_mean_suggestions.length > 0 && ` · ${data.did_you_mean_suggestions.length} did-you-mean suggestion(s)`}
        </div>
        <div className="text-paper-trace">
          Net send count: <span className="text-amber-glow tabular-nums">{fmtInt(data.net_sendable)}</span>
          {' '}(after skipping undeliverable + high-risk)
        </div>
      </div>

      {data.did_you_mean_suggestions.length > 0 && (
        <details className="font-mono text-[10px] text-paper-trace">
          <summary className="cursor-pointer hover:text-amber-glow">
            Typo suggestions ({data.did_you_mean_suggestions.length})
          </summary>
          <div className="mt-1 space-y-0.5 max-h-32 overflow-auto">
            {data.did_you_mean_suggestions.slice(0, 20).map((s) => (
              <div key={s.address}>
                <span className="text-paper-dim">{s.address}</span>
                {' → '}
                <span className="text-radar-cyan">{s.suggestion}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  )
}

function HealthTile({ label, value, tone }: { label: string; value: number; tone: string }) {
  return (
    <div className="border border-ink-500 bg-ink-900/40 px-2 py-1.5">
      <div className="label text-paper-faint">{label}</div>
      <div className={`font-mono text-[16px] tabular-nums ${tone}`}>{fmtInt(value)}</div>
    </div>
  )
}

function FollowUpWavesEditor({
  waves, onChange, templates, disabled, recurringHint,
}: {
  waves: FormWave[]
  onChange: (w: FormWave[]) => void
  templates: EmailTemplate[]
  disabled: boolean
  recurringHint: boolean
}) {
  const update = (i: number, patch: Partial<FormWave>) => {
    onChange(waves.map((w, idx) => (idx === i ? { ...w, ...patch } : w)))
  }
  const add = () => {
    const last = waves.length > 0 ? Number(waves[waves.length - 1].day_offset) || 0 : 0
    onChange([...waves, { day_offset: String(last + 1), template_id: '', subject: '' }])
  }
  const remove = (i: number) => {
    onChange(waves.filter((_, idx) => idx !== i))
  }

  // Per-row diagnostics: complain about the offset only after the user has
  // typed something. We don't validate empty inputs while they're still mid-edit.
  const offsetIssue = (i: number): string | null => {
    const v = Number(waves[i].day_offset)
    if (!waves[i].day_offset.trim()) return null
    if (!Number.isFinite(v) || v < 1) return 'must be ≥ 1'
    if (i > 0) {
      const prev = Number(waves[i - 1].day_offset)
      if (Number.isFinite(prev) && v <= prev) {
        return `must be > ${prev}`
      }
    }
    return null
  }

  return (
    <div className="space-y-3">
      <div className="font-mono text-[11px] text-paper-dim leading-relaxed">
        Re-send to recipients who have not <span className="text-paper">clicked</span>{' '}
        any prior send for this campaign. Each wave fires on Day +N at the same
        wall-clock time as the original send. Leave template / subject empty to
        inherit from the previous wave (or the campaign's primary template).
        Strictly increasing day offsets, max {MAX_FOLLOW_UP_WAVES} waves.
      </div>
      {recurringHint && (
        <div className="font-mono text-[11px] text-paper-trace italic">
          Follow-up waves are only available on one-shot campaigns.
        </div>
      )}
      {waves.length === 0 && !recurringHint && (
        <div className="font-mono text-[11px] text-paper-faint italic">
          No follow-ups configured — campaign sends once and stops.
        </div>
      )}
      {waves.map((w, i) => {
        const issue = offsetIssue(i)
        return (
          <div
            key={i}
            className="grid grid-cols-12 gap-2 items-end border border-ink-500 bg-ink-900/40 p-2"
          >
            <div className="col-span-2">
              <span className="label mb-1.5 block">Days after Day 1</span>
              <input
                type="number"
                min={1}
                value={w.day_offset}
                onChange={(e) => update(i, { day_offset: e.target.value })}
                disabled={disabled}
                className="w-full bg-ink-700 border border-ink-500 font-mono text-[12px] text-paper py-1.5 px-2 focus:outline-none focus:border-amber-glow"
              />
              {issue && (
                <div className="font-mono text-[10px] text-radar-red mt-1">{issue}</div>
              )}
            </div>
            <div className="col-span-4">
              <span className="label mb-1.5 block">Template override</span>
              <select
                value={w.template_id}
                onChange={(e) => update(i, { template_id: e.target.value })}
                disabled={disabled}
                className="w-full bg-ink-700 border border-ink-500 font-mono text-[12px] uppercase tracking-label text-paper py-1.5 px-2 focus:outline-none focus:border-amber-glow"
              >
                <option value="">— same as previous —</option>
                {templates
                  .filter((t) => t.enabled)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.slug})
                    </option>
                  ))}
              </select>
            </div>
            <div className="col-span-5">
              <span className="label mb-1.5 block">Subject override</span>
              <input
                type="text"
                value={w.subject}
                onChange={(e) => update(i, { subject: e.target.value })}
                disabled={disabled}
                placeholder="— same as previous —"
                className="w-full bg-ink-700 border border-ink-500 font-mono text-[12px] text-paper py-1.5 px-2 focus:outline-none focus:border-amber-glow"
              />
            </div>
            <div className="col-span-1 flex justify-end">
              <button
                type="button"
                onClick={() => remove(i)}
                disabled={disabled}
                aria-label={`Remove wave ${i + 1}`}
                className="text-paper-trace hover:text-radar-red font-mono text-[16px] px-2 py-1 disabled:opacity-30"
              >
                ×
              </button>
            </div>
          </div>
        )
      })}
      {waves.length < MAX_FOLLOW_UP_WAVES && (
        <Button
          variant="outline"
          size="sm"
          onClick={add}
          disabled={disabled}
        >
          + Add wave
        </Button>
      )}
    </div>
  )
}

function describeError(err: unknown): string | null {
  if (!err) return null
  if (err instanceof ApiError) {
    if (typeof err.detail === 'object' && err.detail !== null) {
      const d = (err.detail as Record<string, unknown>).detail
      if (typeof d === 'string') return d
    }
    return err.message
  }
  return (err as Error).message ?? 'unknown error'
}
